import { Avatar, Stack, SvgIcon, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { fPercent, formatNumber } from "src/utils/format-number";
import { palette } from "../../figma-palette";
import { ColumnFeatures } from "../features";

export const domain_name = {
  field: "domain_name",
  position: 1,
  headerName: "dashboard.nb_brand_grid.columns.title.domains",
  headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.domains",
  disableColumnMenu: true,
  width: 250,
  type: "string",
  renderCell: (params: GridRenderCellParams) => {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
        <Avatar
          alt={params.row.domain_name}
          src={`https://www.google.com/s2/favicons?domain=${params.row.domain_name}&sz=32`}
          sx={{ width: 18, height: 18 }}
        />
        <Typography variant="body2" noWrap sx={{
          flexGrow: 1,
          color: "#333C47",
          fontFamily: "Public Sans",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "22px",
        }}>
          {params.row.domain_name}
        </Typography>
        <Typography variant="caption" sx={{
          background: "linear-gradient(90deg, rgba(249, 249, 250, 0.00) 0%, #F9F9FA 55.09%);",
          color: "rgba(0, 108, 156, 0.85)",
          padding: "2px 4px",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          cursor: "pointer",
        }}>
          See evolution <SvgIcon component={() => (
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M0.599609 7.26628L7.26628 0.599609M7.26628 0.599609H0.599609M7.26628 0.599609V7.26628" stroke="#006C9C" stroke-opacity="0.85" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          )} />
        </Typography>
      </Stack>
    );
  },
};

export const monthly_searches = {
  field: "monthly_searches",
  position: 2,
  headerName: "dashboard.nb_brand_grid.columns.title.monthly_searches",
  headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.monthly_searches",
  type: "number",
  format: formatNumber,
  features: [ColumnFeatures.COMPARISON],
  renderCell: (params: GridRenderCellParams) => formatNumber(params.row.monthly_searches),
};

export const visibility_rate = {
  field: "visibility_rate",
  position: 3,
  headerName: "dashboard.nb_brand_grid.columns.title.visibility_rate",
  headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.visibility_rate",
  type: "number",
  format: fPercent,
  renderCell: (params: GridRenderCellParams) => {
    const color = palette.visibility_range.find(
      ({ min, max }) => {
        return params.row.visibility_rate >= min && params.row.visibility_rate <= max
      }
    )?.color;

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        py={1}
        sx={{ width: '100%', height: '52px' }}
      >
        <Typography variant="body2">
          {fPercent(params.row.visibility_rate)}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          py={1}
          sx={{ width: '100%', height: '52px' }}
        >
          <Stack
            sx={{
              width: `${params.row.visibility_rate}%`,
              height: '8px',
              backgroundColor: color,
              borderRadius: '6px',
            }}
          />
        </Stack>
      </Stack>
    );
  },
  features: [ColumnFeatures.COMPARISON, ColumnFeatures.SIMPLEBAR],
};

export const paid_visibility_rate = {
  field: "paid_visibility_rate",
  position: 4,
  headerName: "dashboard.nb_brand_grid.columns.title.paid_visibility_rate",
  headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.paid_visibility_rate",
  color: palette.paid.search,
  type: "number",
  format: fPercent,
  features: [ColumnFeatures.COMPARISON, ColumnFeatures.SIMPLEBAR],
  renderCell: (params: GridRenderCellParams) => {
    const color = palette.paidRange.find(
      ({ min, max }) => {
        return params.row.paid_visibility_rate >= min && params.row.paid_visibility_rate <= max
      }
    )?.color;
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        py={1}
        sx={{ width: '100%', height: '52px' }}
      >
        <Typography variant="body2">
          {fPercent(params.row.paid_visibility_rate)}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          py={1}
          sx={{ width: '100%', height: '52px' }}
        >
          <Stack
            sx={{
              width: `${params.row.paid_visibility_rate}%`,
              height: '8px',
              backgroundColor: color,
              borderRadius: '6px',
            }}
          />
        </Stack>
      </Stack>
    );
  },
};

export const organic_visibility_rate = {
  field: "organic_visibility_rate",
  position: 5,
  headerName: "dashboard.nb_brand_grid.columns.title.organic_visibility_rate",
  headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.organic_visibility_rate",
  type: "number",
  format: fPercent,
  features: [ColumnFeatures.COMPARISON, ColumnFeatures.SIMPLEBAR],
  renderCell: (params: GridRenderCellParams) => {
    const color = palette.organicRange.find(
      ({ min, max }) => {
        return params.row.organic_visibility_rate >= min && params.row.organic_visibility_rate <= max
      }
    )?.color;
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        py={1}
        sx={{ width: '100%', height: '52px' }}
      >
        <Typography variant="body2">
          {fPercent(params.row.organic_visibility_rate || 0)}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          py={1}
          sx={{ width: '100%', height: '52px' }}
        >
          <Stack
            sx={{
              width: `${params.row.organic_visibility_rate}%`,
              height: '8px',
              backgroundColor: color,
              borderRadius: '6px',
            }}
          />
        </Stack>
      </Stack>
    );
  },
};

export const paid_search_share = {
    field: "paid_search_share",
    position: 6,
    headerName: "dashboard.nb_brand_grid.columns.title.paid_search_share",
    headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.paid_search_share",
    type: "number",
    format: fPercent,
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    transform: (params: GridRenderCellParams) => ({
      total: params.row.paid_search_share || 0,
      data: [
        {
          name: "dashboard.nb_brand_grid.columns.competitors.1st_position",
          value: params.row.paid_search_share_1st,
          color: palette.paidSearchShare.first,
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.2-3_position",
          value: params.row.paid_search_share_23,
          color: palette.paidSearchShare.then,
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.competitors",
          value: params.row.paid_search_share_compet,
          color: palette.paidSearchShare.compet,
        },
      ],
    }),
  };

  export const paid_shopping_share = {
    field: "paid_shopping_share",
    position: 7,
    headerName: "dashboard.nb_brand_grid.columns.title.paid_shopping_share",
    headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.paid_shopping_share",
    type: "number",
    format: fPercent,
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    transform: (params: GridRenderCellParams) => ({
      total: params.row.paid_shopping_share || 0,
      data: [
        {
          name: "dashboard.nb_brand_grid.columns.competitors.1-3_position",
          value: params.row.paid_pla_share_top,
          color: palette.paidShoppingShare.first,
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.4-10_position",
          value: params.row.paid_pla_share_last,
          color: palette.paidShoppingShare.then,
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.competitors",
          value: params.row.paid_pla_share_compet,
          color: palette.paidShoppingShare.compet,
        },
      ],
    }),
  };

  export const organic_share = {
    field: "organic_share",
    position: 8,
    headerName: "dashboard.nb_brand_grid.columns.title.organic_share",
    headerTooltip: "dashboard.nb_brand_grid.columns.tooltip.organic_share",
    type: "number",
    format: fPercent,
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    transform: (params: GridRenderCellParams) => ({
      total: params.row.organic_share || 0,
      data: [
        {
          name: "dashboard.nb_brand_grid.columns.competitors.1st_position",
          value: params.row.organic_share_first,
          color: palette.organic,
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.2-3_position",
          value: params.row.organic_share_top,
          color: "#6BC497",
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.4-10_position",
          value: params.row.organic_share_last,
          color: "#B7E8CB",
        },
        {
          name: "dashboard.nb_brand_grid.columns.competitors.competitors",
          value: params.row.organic_share_compet,
          color: "#E6F6ED",
        },
      ],
    }),
  };
